// Layer 4 (UI) — the Cost & revenue lead band, per the locked comp
// (`.impeccable/mocks/decision/cost.html`, variant A, picked 25 Sep): the
// period switch, the four-cell margin strip, the operating-cost categories
// ranked by overrun against Budget, and the daily-margin bars. Presentational
// only: every figure arrives from `CostBand.transform.ts` or the route's
// existing transforms.

import { GlassCard, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { TrendChart } from '@/versions/v1/modules/_viz';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard, HOT_ROW, RankBar } from '@/versions/v1/modules/downtime/DowntimeCard';
import { Comparison, StripCell } from '@/versions/v1/modules/downtime/DowntimeImpact';
import { DAILY_MARGIN_META, costOverruns, costStrip, type CostOverrunRow, type CostStripCell } from './CostBand.transform';
import { COST_PERIODS, dailyMargin, type CostCategoryName, type CostPeriod } from './Scene.transform';

/** The period switch: the Downtime window segment's idiom. It sits in the
 *  header on every tab and drives the band on every tab. */
export function CostPeriodSegment({ period, onChange }: { period: CostPeriod; onChange: (period: CostPeriod) => void }) {
  return (
    <div
      role="group"
      aria-label="Cost and revenue period"
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-[3px]"
    >
      {COST_PERIODS.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-pressed={item.id === period}
          data-cost-period={item.id}
          onClick={() => onChange(item.id)}
          className={cn(
            'rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-150',
            item.id === period
              ? 'bg-ink-primary text-[var(--surface-2)]'
              : 'text-ink-secondary hover:bg-[var(--surface-2)] hover:text-ink-primary',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function StripLead({ cell }: { cell: CostStripCell }) {
  if (cell.leadTone === 'warn') {
    return (
      <Comparison>
        <span className="font-semibold" style={{ color: vizToneColor('warn') }}>
          {cell.lead}
        </span>{' '}
        {cell.rest}
      </Comparison>
    );
  }
  return <Comparison lead={cell.lead}> {cell.rest}</Comparison>;
}

function MarginStrip({ period }: { period: CostPeriod }) {
  const cells = costStrip(period);
  return (
    <Commentable name="CostStrip">
      <GlassCard
        tone="matte"
        aria-label="The period's money"
        data-cost-strip
        className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div className="grid grid-cols-2 xl:grid-cols-4">
          {cells.map((cell, index) => (
            <StripCell
              key={cell.name}
              name={cell.name}
              figure={cell.figure}
              unit={cell.unit ?? undefined}
              notch={index < cells.length - 1}
              actors={index === 0 ? <ActorChip actor="Engine">Engine · smelter terms</ActorChip> : undefined}
            >
              <StripLead cell={cell} />
            </StripCell>
          ))}
        </div>
      </GlassCard>
    </Commentable>
  );
}

function deltaStyle(tone: CostOverrunRow['tone']) {
  if (tone === 'warn' || tone === 'ok') return { color: vizToneColor(tone) };
  return undefined;
}

function OverrunCard({ period, onOpenBuildUp }: { period: CostPeriod; onOpenBuildUp: (category: CostCategoryName) => void }) {
  const rows = costOverruns(period);
  return (
    <Commentable name="CostOverruns">
      <div className="min-w-0 xl:col-span-8">
        <DowntimeCard title="Operating cost against Budget" meta="by category · ranked by overrun">
          <div className={CARD_TABLE}>
            <Table wrap={false} className="tabular-nums" aria-label="Operating cost against Budget">
              <THead>
                <TR>
                  <TH className="w-8">#</TH>
                  <TH>Category</TH>
                  <TH className="w-[30%]">Over Budget</TH>
                  <TH className="text-right">Budget → actual</TH>
                  <TH className="text-right">Δ</TH>
                  <TH className="w-[5.75rem] text-right">Drill down</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const hot = row.rank === 1 && row.deltaRaw > 0;
                  return (
                    <TR key={row.category} className={hot ? HOT_ROW : undefined} data-overrun-row={row.category}>
                      {/* Rank 1 takes the crimson; the rest are muted ordinals. The
                      colour is inline so the card table's cell ink cannot
                      override it. */}
                      <TD className="font-semibold" style={{ color: hot ? 'var(--accent)' : 'var(--ink-tertiary)' }}>
                        {row.rank}
                      </TD>
                      <TD className="whitespace-nowrap">
                        <span className="block font-semibold text-ink-primary">{row.category}</span>
                        {row.volumeDriven ? <span className="block text-xs text-muted">volume-driven</span> : null}
                      </TD>
                      <TD>
                        {row.ratio > 0 ? (
                          <RankBar ratio={row.ratio} tone={hot ? 'top' : row.volumeDriven ? 'dim' : 'ink'} />
                        ) : null}
                      </TD>
                      <TD className="whitespace-nowrap text-right text-muted">
                        {row.budget} → <span className="font-semibold text-ink-primary">{row.actual}</span>
                      </TD>
                      <TD
                        data-overrun-delta
                        className={cn(
                          'whitespace-nowrap text-right',
                          row.tone === 'zero' ? 'text-muted' : 'font-semibold',
                          row.tone === 'neutral' ? 'text-ink-primary' : '',
                        )}
                        style={deltaStyle(row.tone)}
                      >
                        {row.delta}
                      </TD>
                      <TD className="text-right">
                        <button
                          type="button"
                          data-build-up={row.category}
                          onClick={() => onOpenBuildUp(row.category)}
                          className="rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2 py-0.5 text-xs font-medium text-ink-secondary transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary"
                        >
                          Build-up
                        </button>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

/** The seven days' margin as neutral bars under the dashed design-potential
 *  reference. It reads the seven seeded days on every period, as it always
 *  has. */
function DailyMarginCard() {
  const margin = dailyMargin();
  return (
    <Commentable name="CostMarginMeter">
      <div className="min-w-0 xl:col-span-4">
        <DowntimeCard title="Daily margin" meta={DAILY_MARGIN_META}>
          <div data-comment-anchor="CostScene.CostTrend">
            <TrendChart
              kind="bar"
              categories={margin.categories}
              series={margin.series}
              references={margin.references}
              barFill="series"
              height={172}
            />
          </div>
          <p className="mt-0.5 text-xs text-muted">dashed = design potential · Jun</p>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

export function CostBand({ period, onOpenBuildUp }: { period: CostPeriod; onOpenBuildUp: (category: CostCategoryName) => void }) {
  return (
    <section aria-label="Cost and revenue band" className="space-y-3">
      <MarginStrip period={period} />
      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
        <OverrunCard period={period} onOpenBuildUp={onOpenBuildUp} />
        <DailyMarginCard />
      </div>
    </section>
  );
}
